import React, { useRef } from 'react';
import { X } from 'lucide-react';

export default function ClearableField({
  as = 'input',
  value,
  onChange,
  onClear,
  style = {},
  wrapperStyle = {},
  theme,
  clearable = true,
  children,
  ...props
}) {
  const Tag = as;
  const fieldRef = useRef(null);
  const stringValue = value ?? '';
  const hasValue = String(stringValue).length > 0;
  const showClear = clearable && hasValue && props.type !== 'password' && props.type !== 'file' && props.type !== 'range' && props.type !== 'color';
  const needsSelectPadding = as === 'select';
  const mergedStyle = {
    ...style,
    paddingRight: showClear ? (needsSelectPadding ? '44px' : '32px') : style.paddingRight,
  };

  // The visible pill stays 18px; only the invisible hit box around it grows.
  // The button is transparent and the pill moved to an inner span, and the
  // `right` offset is pulled back by exactly half the added width so the pill
  // lands on the same pixel as before in every caller. Width stays inside the
  // field's own paddingRight (32px / 44px), so the hit box never reaches the
  // text — and for selects it stops short of the native dropdown arrow.
  const PILL = 18;
  const hitW = needsSelectPadding ? 24 : 28;
  const hitH = 28;
  const pillRight = needsSelectPadding ? 22 : 6;
  const hitRight = pillRight - (hitW - PILL) / 2;

  return (
    <div style={{ position: 'relative', ...wrapperStyle }}>
      <Tag ref={fieldRef} value={stringValue} onChange={onChange} style={mergedStyle} {...props}>
        {children}
      </Tag>
      {showClear && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Focus the field BEFORE clearing: clearing empties the value, which
            // unmounts this button, and React does not relocate focus on unmount —
            // a keyboard user would otherwise be dropped back to <body>.
            fieldRef.current?.focus();
            if (onClear) onClear();
            else if (onChange) onChange({ target: { value: '' } });
          }}
          title="Clear"
          aria-label="Clear"
          style={{
            position: 'absolute',
            right: `${hitRight}px`,
            top: '50%',
            transform: 'translateY(-50%)',
            width: `${hitW}px`,
            height: `${hitH}px`,
            border: 'none',
            background: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            padding: 0,
            lineHeight: 1,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: `${PILL}px`,
              height: `${PILL}px`,
              borderRadius: '999px',
              background: theme?.border ? `${theme.border}66` : 'rgba(148,163,184,0.35)',
              color: theme?.textSub || '#64748b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <X size={11} strokeWidth={2.4} />
          </span>
        </button>
      )}
    </div>
  );
}
